import tables, {PAGE_SIZES} from 'constants/tables'
import {useEffect, useState} from 'react'
import {useUrlParams} from 'services/hooks'
import {getItemId, getTableData} from 'services/utils/tableUtils'
import {onDelete} from 'services/utils/toastUtils'
import {useTranslation} from 'react-i18next'

function objectToURLParams(obj) {
    const params = new URLSearchParams()
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (obj[key]) params.append(key, obj[key])
        }
    }
    return params.toString()
}

const useTable = (
    itemName,
    useGetQuery,
    paginationType,
    useDeleteMutation,
    visibleKeys,
    allowExpand
) => {
    const tableName = `${itemName}s`

    const {t} = useTranslation()

    const {urlParams} = useUrlParams()
    const {
        search,
        [`${itemName}_page`]: page,
        [`${itemName}_pageSize`]: pageSize,
        [`${itemName}_sort`]: sort,
        ...resParams
    } = urlParams

    // If no sort in URL yet, use the table's default desc sort key so the first
    // query already has the correct params — preventing a duplicate API call when
    // useTableSort's mount effect sets the default sort after the first render.
    const defaultSortKey = tables[tableName]?.sortTableKeysDefaultDesc?.[0]
    const effectiveSort =
        sort || (defaultSortKey ? JSON.stringify({[defaultSortKey]: 'desc'}) : undefined)

    const [filtersRequested, setFiltersRequested] = useState(false)
    const isExternal = paginationType === 'external'

    const {data, isFetching: isLoadingTableData} = useGetQuery(
        isExternal
            ? objectToURLParams({
                  page: page || 0,
                  pageSize: pageSize || PAGE_SIZES[2],
                  search,
                  sort: effectiveSort,
                  includeTotal: 'false',
                  ...resParams,
              })
            : {}
    )

    // Filter options are heavy for the server and only needed once the filter popover opens —
    // they never change while paging/sorting/searching, so they're fetched separately and lazily.
    const {data: filtersData, isFetching: isLoadingFilters} = useGetQuery('filtersOnly=true', {
        skip: !isExternal || !filtersRequested,
    })

    const [errorMessage, setErrorMessage] = useState(null)
    const [doDelete, {errorDelete}] = useDeleteMutation()

    useEffect(() => {
        setErrorMessage(errorDelete?.data?.error)
    }, [errorDelete])

    const {
        tableKeys,
        tableData,
        keyTypes,
        tableItems,
        totalItems,
        hasMore,
        filters: listFilters,
        filterKeys: listFilterKeys,
        keyTranslate,
        externalKeys,
    } = getTableData(tableName, visibleKeys, data, page, pageSize, search, sort, paginationType)

    const filters = filtersData?.filters ?? listFilters
    const filterKeys = listFilterKeys ?? Object.keys(listFilters || {})

    const onDeleteHandler = (itemData) => onDelete(itemData, itemName, doDelete, t)

    const [expandedId, setExpandedId] = useState(null)

    const handleExpand = () => (allowExpand ? setExpandedId : null)

    const getExpandedItem = () => tableItems.find((item) => getItemId(item) === expandedId)

    return {
        tableName,
        tableKeys,
        tableData,
        keyTypes,
        keyTranslate,
        externalKeys,
        tableItems,
        totalItems,
        hasMore,
        errorMessage,
        expandedId,
        filters,
        filterKeys,
        isLoadingFilters,
        requestFilters: () => setFiltersRequested(true),
        doDelete: onDeleteHandler,
        handleExpand,
        getExpandedItem,
        isLoadingTableData,
    }
}

export default useTable
